import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { Users, UserCheck, BookOpen, Layers, FileQuestion, Brain, ShieldCheck, Loader2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, color = "blue", testId }) => (
  <div className="p-6 bg-white border border-slate-200 rounded-md hover:shadow-md transition-shadow" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className={`h-10 w-10 rounded-md grid place-items-center bg-${color}-50 text-${color}-700`}>
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
    </div>
    <div className="mt-4 text-3xl font-display font-bold tracking-tight">{value}</div>
    <div className="text-sm text-slate-500 mt-1">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const loadStats = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const cleanupOrphans = async () => {
    setCleanupBusy(true);
    try {
      const r = await api.delete("/admin/maintenance/clear-orphans");
      const d = r.data || {};
      const total = (d.deleted_subjects || 0) + (d.deleted_modules || 0) + (d.deleted_questions || 0) + (d.deleted_ai_explanations || 0);
      if (total === 0) {
        toast.success("No orphan records found — dashboard is already clean.");
      } else {
        toast.success(
          `Removed ${total} orphan record(s): ` +
          `${d.deleted_subjects} subject(s), ${d.deleted_modules} module(s), ${d.deleted_questions} question(s), ${d.deleted_ai_explanations} AI explanation(s).`
        );
      }
      await loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  if (!stats) return <div className="text-slate-500">Loading dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Overview</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Dashboard</h1>
          <p className="text-slate-600 mt-1">Welcome to PrepMasters control center.</p>
        </div>
        {user?.role === "super_admin" && (
          <Button
            variant="outline"
            className="h-9 border-amber-300 text-amber-800 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/30"
            onClick={cleanupOrphans}
            disabled={cleanupBusy}
            data-testid="clear-orphans-btn"
          >
            {cleanupBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wrench className="w-4 h-4 mr-1.5" />}
            Clean up orphan data
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 stagger">
        <StatCard icon={Users} label="Total Students" value={stats.total_students} testId="stat-total-students" />
        <StatCard icon={UserCheck} label="Pending" value={stats.pending_students} color="amber" testId="stat-pending-students" />
        <StatCard icon={UserCheck} label="Approved" value={stats.approved_students} color="green" testId="stat-approved-students" />
        <StatCard icon={ShieldCheck} label="Sub-Admins" value={stats.total_sub_admins} color="indigo" testId="stat-sub-admins" />
        <StatCard icon={BookOpen} label="Courses" value={stats.total_courses} testId="stat-courses" />
        <StatCard icon={Layers} label="Subjects" value={stats.total_subjects} testId="stat-subjects" />
        <StatCard icon={Layers} label="Modules" value={stats.total_modules} testId="stat-modules" />
        <StatCard icon={FileQuestion} label="Questions" value={stats.total_questions} testId="stat-questions" />
        <StatCard icon={Brain} label="With AI" value={stats.questions_with_ai} color="indigo" testId="stat-ai-yes" />
        <StatCard icon={Brain} label="Without AI" value={stats.questions_without_ai} color="slate" testId="stat-ai-no" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-bold text-lg">Student Registration Trend</h3>
          <p className="text-sm text-slate-500 mb-4">Last 7 days</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.registration_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-bold text-lg">Questions per Course</h3>
          <p className="text-sm text-slate-500 mb-4">Content volume across courses</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.course_stats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="questions" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
